import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';

// components
import KitchenZipCodeLookup from './KitchenZipCodeLookup';
import PageContent from "./PageContent";
import KitchenReadiness from './chef/KitchenReadiness';
import CustomerReadiness from './customer/CustomerReadiness';

// mui
import Grid from '@mui/material/Grid';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// removed unused CheckCircleIcon

import { useUser } from '../UserContext';
import { useAuth } from '../AuthContext';
import { useLocale } from '../i18n/LanguageContext';
import { KitchenProvider, useKitchen } from '../contexts/KitchenContext';
import MyMap from './Maps/MyMap';
import LoginWelcomeVerbiage from './Login/LoginWelcomeVerbiage';
import { calculateDistance, filterLocationsByDistance } from '../utils/locationUtils';
import { Utils } from '../utils/utils';
import { getKitchenSetupData } from '../services/kitchenService';
import KitchenCurrentStatus from './chef/Kitchen/KitchenCurrentStatus';
import StateRegulationReferences from './chef/StateRegulationReferences';
import OrderStatusFlowDiagram from './shared/OrderStatusFlowDiagram';

const DEFAULT_MAX_SEARCH_RADIUS_MILES = 12;
const EXTENDED_SEARCH_RADIUS_MILES = 50;

function ScrollToTopOnRouteChange() {
  const location = useLocation();

  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch (error) {
      try {
        window.scrollTo(0, 0);
      } catch (nestedError) {
      }
    }

    if (document?.documentElement) {
      document.documentElement.scrollTop = 0;
    }

    if (document?.body) {
      document.body.scrollTop = 0;
    }
  }, [location.pathname, location.search]);

  return null;
}

function HomeContent() {

  const { user } = useUser();
  const { auth } = useAuth();
  const { t } = useLocale();
  const { refreshTrigger, currentKitchenData, setCurrentKitchenData } = useKitchen();
  const [coords, setCoords] = useState({});
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [displayLocations, setDisplayLocations] = useState([]);
  const [isBeyondSearchRadius, setIsBeyondSearchRadius] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [maxDistance, setMaxDistance] = useState(6); // Default 6 miles
  const [extendedDistanceEnabled, setExtendedDistanceEnabled] = useState(false);
  const [chefsExpanded, setChefsExpanded] = useState(false);
  const [orderFlowDialogOpen, setOrderFlowDialogOpen] = useState(false);

  function handleKitchensInMap(kitchenList) {
    setLocations(kitchenList);
  }

  const handleDistanceChange = (event, newValue) => {
    setExtendedDistanceEnabled(false);
    setMaxDistance(newValue);
  };

  const handleEnableExtendedDistance = () => {
    setExtendedDistanceEnabled(true);
    setMaxDistance(EXTENDED_SEARCH_RADIUS_MILES);
  };

  const handleMapRadiusChange = (radiusInMiles) => {
    const normalizedRadius = Math.max(1, Math.min(EXTENDED_SEARCH_RADIUS_MILES, radiusInMiles));

    if (normalizedRadius <= DEFAULT_MAX_SEARCH_RADIUS_MILES) {
      if (normalizedRadius < DEFAULT_MAX_SEARCH_RADIUS_MILES) {
        setExtendedDistanceEnabled(false);
      }

      setMaxDistance((currentDistance) => (
        currentDistance === normalizedRadius ? currentDistance : normalizedRadius
      ));
      return;
    }

    if (extendedDistanceEnabled || filteredLocations.length === 0) {
      setExtendedDistanceEnabled(true);
      setMaxDistance((currentDistance) => (
        currentDistance === normalizedRadius ? currentDistance : normalizedRadius
      ));
    }
  };

  // Fetch kitchen data for chefs
  useEffect(() => {
    const fetchKitchenData = async () => {
      try {
          const data = await getKitchenSetupData(user.chef.id, auth);
          setCurrentKitchenData(data);
        } catch (error) {
          console.error('Error fetching kitchen data:', error);
        }
    };

    if (user && Utils.isChef(user)) {
        fetchKitchenData();
    }
    
  }, [user, auth, refreshTrigger]);
  
  // Filter locations when coords, locations, or maxDistance change
  useEffect(() => {
    if (coords.latitude && coords.longitude && locations.length > 0) {
      const normalizedMaxDistance = Math.max(1, Math.min(EXTENDED_SEARCH_RADIUS_MILES, maxDistance));
      const isExtendedRadius = normalizedMaxDistance > DEFAULT_MAX_SEARCH_RADIUS_MILES;
      const nearby = filterLocationsByDistance(
        locations, 
        coords.latitude, 
        coords.longitude, 
        normalizedMaxDistance,
        100  // max number of results
      );

      setFilteredLocations(nearby);

      if (nearby.length > 0) {
        setDisplayLocations(nearby);
        setIsBeyondSearchRadius(isExtendedRadius);
        return;
      }

      const withinTwelveMiles = filterLocationsByDistance(
        locations,
        coords.latitude,
        coords.longitude,
        DEFAULT_MAX_SEARCH_RADIUS_MILES,
        100
      );

      const allWithDistance = locations
        .map((location) => ({
          ...location,
          distance: calculateDistance(coords.latitude, coords.longitude, location.latitude, location.longitude),
        }))
        .filter((location) => location.distance <= EXTENDED_SEARCH_RADIUS_MILES)
        .sort((a, b) => a.distance - b.distance);

      if (withinTwelveMiles.length === 0 && allWithDistance.length > 0) {
        setDisplayLocations(allWithDistance);
        setIsBeyondSearchRadius(true);
      } else {
        setDisplayLocations([]);
        setIsBeyondSearchRadius(false);
      }
    } else {
      setFilteredLocations([]);
      setDisplayLocations([]);
      setIsBeyondSearchRadius(false);
    }
  }, [coords, locations, maxDistance]);
  
  useEffect(() => {

    (() => {
      navigator.geolocation.getCurrentPosition((position) => {
      setCoords(position.coords)
     }, function (e) {
      setLocationError('Error: ' + e.message);
     });

    })();

  }, []);

  return (
    <Router>
      <ScrollToTopOnRouteChange />
      <Grid container spacing={1}>
          <Grid item xs={12} lg={1} xl={2}></Grid>
          
          {/* Sidebar - order changes based on screen size */}
          <Grid item xs={12} md={4} lg={3} xl={2} ml={2} mr={2} sx={{ order: { xs: 2, lg: 0 }, pb: { xs: 2, lg: 0 }, display: { xs: user ? 'block' : 'none', md: 'block' } }}>
            { user ? (
              <>  
              { Utils.isChef(user) ? (
                <div style={{ color: 'gray'}}>
                  <KitchenReadiness user={user} kitchenData={currentKitchenData} />
                  <KitchenCurrentStatus kitchenData={currentKitchenData} />
                  <StateRegulationReferences kitchenData={currentKitchenData} />
                </div>
              ) : (
                <div style={{ color: 'gray'}}>
                  <CustomerReadiness user={user} />
                  
                  {/* <KitchenZipCodeLookup /> */}
                  
                  {/* Distance Filter Slider */}
                  <div style={{ marginTop: 16, marginBottom: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                      <Typography variant="body2">
                        Search Radius: {isBeyondSearchRadius ? `${DEFAULT_MAX_SEARCH_RADIUS_MILES}+ miles away` : `${maxDistance} miles`}
                      </Typography>
                      {!extendedDistanceEnabled && Math.min(maxDistance, DEFAULT_MAX_SEARCH_RADIUS_MILES) === DEFAULT_MAX_SEARCH_RADIUS_MILES && (
                        <Button
                          size="small"
                          variant="text"
                          style={{ textTransform: 'none', minWidth: 'auto', padding: '2px 6px' }}
                          onClick={handleEnableExtendedDistance}
                          data-testid="distance-12plus-toggle"
                        >
                          {DEFAULT_MAX_SEARCH_RADIUS_MILES}+ miles
                        </Button>
                      )}
                    </div>
                    <Slider
                      value={Math.min(maxDistance, DEFAULT_MAX_SEARCH_RADIUS_MILES)}
                      onChange={handleDistanceChange}
                      min={1}
                      max={DEFAULT_MAX_SEARCH_RADIUS_MILES}
                      step={1}
                      marks={[
                        { value: 1, label: '1' },
                        { value: 3, label: '3' },
                        { value: 6, label: '6' },
                        { value: 9, label: '9' },
                        { value: DEFAULT_MAX_SEARCH_RADIUS_MILES, label: `${DEFAULT_MAX_SEARCH_RADIUS_MILES}` },
                      ]}
                      valueLabelDisplay="auto"
                      aria-label="Search radius in miles"
                      data-testid="distance-slider"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {isBeyondSearchRadius
                        ? (filteredLocations.length === 0
                          ? `No kitchens within ${DEFAULT_MAX_SEARCH_RADIUS_MILES} miles. Showing ${displayLocations.length} farther kitchen(s).`
                          : `Showing ${displayLocations.length} kitchen(s), including results beyond ${DEFAULT_MAX_SEARCH_RADIUS_MILES} miles (up to ${EXTENDED_SEARCH_RADIUS_MILES}).`)
                        : `Showing ${filteredLocations.length} kitchen(s) within ${maxDistance} miles`}
                    </Typography>
                  </div>

                  { locationError ? (
                    <div>{locationError}</div>
                  ) : (
                    coords && coords.latitude && coords.longitude ? (
                      <MyMap
                        coords={coords}
                        locations={displayLocations}
                        onMapRadiusChange={handleMapRadiusChange}
                      />
                    ) : (
                      <div>Loading geolocation...</div>
                    )
                  )}      
                </div>
              )}
              </> 
            ) : (
              <LoginWelcomeVerbiage />
            )}
              
          </Grid>
          
          {/* Main Content - order changes based on screen size */}
          <Grid item xs={12} md={8} lg={7} xl={6} sx={{ order: { xs: 1, lg: 0 }, pt: { xs: 2, lg: 0 } }}>
              <PageContent 
                onKitchenListRetrieval={handleKitchensInMap} 
                filteredLocations={displayLocations}
                userCoords={coords}
              />
              {/* end main column content */}
          </Grid>
          <Grid item xs={12} lg={1} xl={2}></Grid>

          {/* Full-width footer tiles */}
          <Grid item xs={12} sx={{ order: { xs: 3, lg: 0 } }}>
            <Box sx={{ bgcolor: 'background.paper', py: 6 }}>
              <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2 }}>
                <Grid container spacing={3}>

                  <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 1, '&:hover': { backgroundColor: 'action.hover' } }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>{t('home.tiles.chefs.title')}</Typography>
                        <List dense>
                          {(() => {
                            const chefBullets = [
                              t('home.tiles.chefs.bullet2'),
                              t('home.tiles.chefs.bullet3'),
                              t('home.tiles.chefs.bullet8'),
                              t('home.tiles.chefs.bullet5'),
                              t('home.tiles.chefs.bullet6'),
                              t('home.tiles.chefs.bullet7'),
                              t('home.tiles.chefs.bullet4'),
                            ];

                            const visible = chefsExpanded ? chefBullets : chefBullets.slice(0, 4);

                            return visible.map((text) => (
                              <ListItem key={text} disableGutters sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 28, mr: 1 }}>
                                  <CheckCircleIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={text} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', sx: { lineHeight: 1.2 } }} />
                              </ListItem>
                            ));
                          })()}
                        </List>
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          variant="text"
                          sx={{ textTransform: 'none' }}
                          onClick={() => setChefsExpanded((s) => !s)}
                        >
                          {chefsExpanded ? 'Show less' : 'Show more'}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 1, '&:hover': { backgroundColor: 'action.hover' } }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom style={{ marginBottom: 20 }}>{t('home.tiles.customer.title')}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                          {t('home.tiles.customer.description')}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button
                          size="small"
                          variant="text"
                          sx={{ textTransform: 'none' }}
                          onClick={() => setOrderFlowDialogOpen(true)}
                          data-testid="customer-order-flow-button"
                        >
                          {t('home.tiles.customer.cta')}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 1, '&:hover': { backgroundColor: 'action.hover' } }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom style={{ marginBottom: 20 }}>{t('home.tiles.payments.title')}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                          {t('home.tiles.payments.description')}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        {/* <Button size="small" variant="text" sx={{ textTransform: 'none' }}>{t('home.tiles.payments.cta')}</Button> */}
                      </CardActions>
                    </Card>
                  </Grid>

                </Grid>
              </Box>
            </Box>

            <Dialog
              open={orderFlowDialogOpen}
              onClose={() => setOrderFlowDialogOpen(false)}
              fullWidth
              maxWidth="md"
              data-testid="order-status-flow-dialog"
            >
              <DialogTitle>Order Status Flow</DialogTitle>
              <DialogContent dividers>
                <OrderStatusFlowDiagram />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOrderFlowDialogOpen(false)} sx={{ textTransform: 'none' }}>
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
      </Grid>
    </Router>
  );
}

export default function Home(props) {
  const { user } = useUser();

  // Only wrap with KitchenProvider for chef users
  if (user && Utils.isChef(user)) {
    return (
      <KitchenProvider>
        <HomeContent />
      </KitchenProvider>
    );
  }

  // Customer users don't need the provider
  return <HomeContent />;
}